import React, { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const AuthTrendChart = ({ data, loading = false, height = '220px' }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 1. 計算時間範圍 (根據用戶需求: 20260427 10:00 ~ 20260428 09:00)
  const { minTs, maxTs } = useMemo(() => {
    const max = new Date();
    max.setHours(max.getHours(), 0, 0, 0); // 今日 09:00
    const min = new Date(max.getTime() - 23 * 60 * 60 * 1000); // 往回推 23 小時，即昨日 10:00
    return { minTs: min.getTime(), maxTs: max.getTime() };
  }, []);

  // 2. 資料清洗與轉換 (針對 Mock 數據進行時間平移)
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    let processed = data
      .map(item => {
        if (Array.isArray(item) && item.length >= 2) {
          // UTC
          const rawTs = item[0];
          const ts = typeof rawTs === 'string'
            ? new Date(rawTs + 'Z').getTime()
            : new Date(rawTs).getTime();
          return isNaN(ts) ? null : [ts, item[1]];
        }
        if (typeof item === 'object' && item !== null) {
          const rawTs = item.time || item.timestamp;
          const ts = typeof rawTs === 'string'
            ? new Date(rawTs + 'Z').getTime()
            : new Date(rawTs).getTime();
          const val = item.value !== undefined ? item.value : item.P95sec;
          return isNaN(ts) ? null : [ts, val];
        }
        return null;
      })
      .filter(item => item !== null)
      .sort((a, b) => a[0] - b[0]);

    if (processed.length > 0) {
      const lastTs = processed[processed.length - 1][0];
      if (maxTs - lastTs > 48 * 3600 * 1000) {
        const offset = maxTs - (lastTs + 3600 * 1000);
        return processed.map(d => [d[0] + offset, d[1]]);
      }
    }

    return processed;
  }, [data, maxTs]);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    let chart = echarts.getInstanceByDom(chartDom);
    if (chart) {
      chart.dispose();
    }
    chartDom.innerHTML = '';

    chart = echarts.init(chartDom, null, { renderer: 'svg' });
    chartInstance.current = chart;

    if (loading || chartData.length === 0) {
      return;
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.palette.primary.greyPrimaryTransparency,
        borderColor: 'transparent',
        padding: [8, 12],
        borderRadius: 4,
        textStyle: { color: '#FFFFFF' },
        formatter: (params) => {
          if (!params || params.length === 0) return "";
          const date = new Date(params[0].value[0]);
          const hh = String(date.getHours()).padStart(2, '0');
          const mm = String(date.getMinutes()).padStart(2, '0');
          return `
            <div style="font-size: 12px; line-height: 1.6;">
              <div style="opacity: 0.9;">${hh}:${mm}</div>
              <div style="display: flex; align-items: center;">
                <span style="opacity: 0.9;">Connect Time :&nbsp;</span>
                <span style="font-weight: bold;">${params[0].value[1]}s</span>
              </div>
            </div>
          `;
        }
      },
      grid: {
        top: 20,
        left: 50,   // 增加左側空間
        right: 25,
        bottom: 40, // 增加底部空間
        containLabel: false
      },
      xAxis: {
        type: 'value',
        min: minTs,
        max: maxTs,
        interval: 4 * 3600 * 1000,
        axisLabel: {
          formatter: (value) => {
            const date = new Date(value);
            return `${date.getHours().toString().padStart(2, '0')}:00`;
          },
          margin: 12, // 標籤離軸線遠一點
          fontSize: 10,
          color: '#A0AEC0'
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#e0e0e0' }
        },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: 'dotted', color: '#EDF2F7' }
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (value) => {
          const ceilMax = Math.ceil(value.max / 20) * 20;
          return ceilMax > 0 ? ceilMax : 100;
        },
        axisLabel: {
          formatter: '{value}s',
          margin: 12, // 標籤離軸線遠一點
          fontSize: 10,
          color: '#A0AEC0'
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#e0e0e0' }
        },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: 'dotted', color: '#EDF2F7' }
        }
      },
      series: [{
        data: chartData,
        type: 'line',
        smooth: false,
        showSymbol: false,
        lineStyle: { color: theme.palette.primary.main, width: 1.5 },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(135, 162, 226, 0.25)' },
            { offset: 1, color: 'rgba(135, 162, 226, 0.02)' }
          ])
        }
      }]
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [chartData, loading, minTs, maxTs, theme]);

  return (
    <Box sx={{ width: '100%', height, position: 'relative' }}>
      <Box
        ref={chartRef}
        sx={{
          width: '100%',
          height: '100%',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s'
        }}
      />
    </Box>
  );
};

export default AuthTrendChart;